
import { useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import { useGlobalContext } from "../../../Config";
import { Dialog } from 'primereact/dialog';
import { guardarPdf } from '../../../services/ContratoService.js';
import { MAX_TAMANO_PDF_BYTES, formatoMB } from '../../../utilities/fileValidations.js';

const FormSubirDocumentoFinal = ({
    isFormVisible,
    setIsFormVisible,
    fetchDocumentos,
    idDocumento,
    idContrato,
    idTipoDocumento,
}) => {
    const toast = useRef(null);
    const [totalSize, setTotalSize] = useState(0);
    const fileUploadRef = useRef(null);
    const { urlContratos } = useGlobalContext();
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);

    const onTemplateSelect = (e) => {
        const file = e.files?.[0];
        if (!file) return;

        const isPdf =
            file.type === 'application/pdf' ||
            /\.pdf$/i.test(file.name);

        if (!isPdf) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Archivo no permitido',
                detail: 'Solo se aceptan documentos .pdf',      
                life: 3000
            });
            fileUploadRef.current?.clear();
            setTotalSize(0);
            setSelectedFile(null);
            return;
        }

        setTotalSize(file.size || 0);
        setSelectedFile(file);
    };

    const onTemplateRemove = (file, callback) => {
        setTotalSize(totalSize - file.size);
        callback();
    };

    const onTemplateClear = () => {
        setTotalSize(0);
        setSelectedFile(null);
    };

    const headerTemplate = (options) => {
        const { className, chooseButton /* , uploadButton, cancelButton */ } = options;
        const value = totalSize / 10000;
        const formatedValue = fileUploadRef && fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : '0 B';

        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
                {/* uploadButton y cancelButton eliminados */}
                <div className="flex align-items-center gap-3 ml-auto">
                    <span>{formatedValue} / {formatoMB(MAX_TAMANO_PDF_BYTES)}</span>
                    <ProgressBar value={value} showValue={false} style={{ width: '10rem', height: '12px' }} />
                </div>
            </div>
        );
    };

    const itemTemplate = (file, props) => {
        return (
            <div className="flex align-items-center flex-wrap">
                <div className="flex align-items-center" style={{ width: '40%' }}>
                    <i className="pi pi-file mr-2" style={{ fontSize: '1.5rem' }} />
                    <span className="flex flex-column text-left ml-3">
                        {file.name}
                        <small>{new Date().toLocaleDateString()}</small>
                    </span>
                </div>
                <Tag value={props.formatSize} severity="warning" className="px-3 py-2" />
                <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-outlined p-button-rounded p-button-danger ml-auto"
                    onClick={() => onTemplateRemove(file, props.onRemove)}
                />
            </div>
        );
    };

    const emptyTemplate = () => {
        return (
            <div className="flex align-items-center flex-column">
                <i className="pi pi-file-pdf mt-3 p-5" style={{ fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}></i>
                <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
                    Arrastra y suelta el PDF aquí
                </span>
            </div>
        );
    };

    const chooseOptions = { icon: 'pi pi-fw pi-file-pdf', iconOnly: true, className: 'custom-choose-btn p-button-rounded p-button-outlined p-button-danger' };

    // Botón Guardar: envía la plantilla al backend
    const handleGuardar = async () => {
        try {
            setLoading(true);  

            if (!selectedFile) {
                toast.current?.show({ severity: 'warn', summary: 'Archivo requerido', detail: 'Seleccione un archivo .pdf', life: 3000 });
                return;
            }

            const formData = new FormData();
            formData.append('idDocumento', idDocumento);
            formData.append('idContrato', idContrato);
            formData.append('idTipoDocumento', idTipoDocumento);
            formData.append('filename', selectedFile.name);
            formData.append('idUsuario', sessionStorage.getItem('usuario_id') || '');
            formData.append('file', selectedFile);

            await guardarPdf(urlContratos, formData);
        
            fileUploadRef.current?.clear();

            await fetchDocumentos();
            toast.current?.show({ severity: 'success', summary: 'Éxito', detail: 'Plantilla guardada', life: 3000 });
        } catch (err) {
            console.error(err);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el documento final', life: 4000 });
        } finally {
            setSelectedFile(null);
            setTotalSize(0);
            setIsFormVisible(false);
            setLoading(false);  
        }
    };

    const resetFormPlantilla = () => {
        setSelectedFile(null);
        setTotalSize(0);
    };

    const handleDialogHide = () => {
        resetFormPlantilla();
        setIsFormVisible(false);
    };

    return (
        <Dialog
            visible={isFormVisible}
            onHide={handleDialogHide}
            style={{ width: "50vw"}}
            closable={true}
            modal
            header="Subir Documento Final"
        >
 
            <div className="card">
                <Toast ref={toast} />
                <Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
                <span className="p-float-label mt-2 mb-3">
                    <h6>
                        Seleccione el Documento Final en formato PDF correspondiente al borrador 
                        del contrato N.º {idContrato}.  
                        Este archivo debe estar aprobado y, al subirlo, el borrador será considerado 
                        como finalizado.
                    </h6>
                </span>
                <FileUpload
                    ref={fileUploadRef}
                    name="documento"
                    url="/api/upload"
                    multiple={false}
                    accept=".pdf,application/pdf"
                    maxFileSize={MAX_TAMANO_PDF_BYTES}
                    onSelect={onTemplateSelect}
                    onError={onTemplateClear}
                    onClear={onTemplateClear}
                    headerTemplate={headerTemplate}
                    itemTemplate={itemTemplate}
                    emptyTemplate={emptyTemplate}
                    chooseOptions={chooseOptions}
                    disabled={loading}
                />

            
            </div>
            <div className="flex justify-content-end mt-3">
                <Button
                    label="Guardar"
                    icon={loading ? "pi pi-spin pi-spinner" : "pi pi-save"}
                    className="p-button-success p-button-rounded p-button"
                    raised
                    onClick={handleGuardar}
                    disabled={!selectedFile || loading}
                />
            </div>
        </Dialog>
    );
}
export default FormSubirDocumentoFinal;
